import React, { useState } from 'react';
import {
    FiBook, FiUsers, FiRefreshCw, FiBarChart2, FiShield,
    FiSettings, FiZap, FiCpu, FiDollarSign, FiGrid,
    FiCheckCircle, FiXCircle, FiArrowRight, FiInfo,
    FiAlertTriangle, FiMenu, FiX, FiChevronRight,
    FiUser, FiSearch, FiHash, FiCalendar, FiEdit2,
    FiTrash2, FiEye, FiPlusCircle, FiStar
} from 'react-icons/fi';
import './HelpCenter.css';

const SECTIONS = [
    { id: 'overview',      icon: <FiGrid />,       label: 'App Overview',       badge: 'Start Here' },
    { id: 'dashboard',     icon: <FiGrid />,       label: 'Dashboard' },
    { id: 'books',         icon: <FiBook />,       label: 'Books & Inventory',  badge: 'Core' },
    { id: 'members',       icon: <FiUsers />,      label: 'Member Management' },
    { id: 'issue-return',  icon: <FiRefreshCw />,  label: 'Issue & Return',     badge: 'Core' },
    { id: 'collection',    icon: <FiDollarSign />, label: 'Collections / Fines' },
    { id: 'reports',       icon: <FiBarChart2 />,  label: 'Reports & Analytics' },
    { id: 'settings',      icon: <FiSettings />,   label: 'Settings',           badge: 'Admin' },
    { id: 'users',         icon: <FiShield />,     label: 'User Management',    badge: 'Admin' },
    { id: 'leo',           icon: <FiCpu />,        label: 'Leo AI Assistant',   badge: 'AI' },
    { id: 'roles',         icon: <FiShield />,     label: 'Roles & Permissions' },
    { id: 'faq',           icon: <FiInfo />,       label: 'FAQ' },
];

/* ─── Reusable sub-components ─── */
const Step = ({ n, title, children }) => (
    <div className="hc-step">
        <div className="hc-step__num">{n}</div>
        <div className="hc-step__body">
            <h4 className="hc-step__title">{title}</h4>
            <div className="hc-step__desc">{children}</div>
        </div>
    </div>
);

const Tip = ({ children }) => (
    <div className="hc-tip"><FiZap className="hc-tip__icon" /><p>{children}</p></div>
);

const Warn = ({ children }) => (
    <div className="hc-warn"><FiAlertTriangle className="hc-warn__icon" /><p>{children}</p></div>
);

const SectionTitle = ({ eyebrow, title }) => (
    <div className="hc-sec-title">
        <span className="hc-eyebrow">{eyebrow}</span>
        <h1 className="hc-h1">{title}</h1>
        <div className="hc-divider" />
    </div>
);

const FieldTable = ({ rows }) => (
    <div className="hc-table-wrap">
        <table className="hc-table">
            <thead><tr><th>Field</th><th>Description</th><th>Required</th></tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={i}>
                    <td><code>{r.field}</code></td>
                    <td>{r.desc}</td>
                    <td><span className={`hc-req hc-req--${r.req === 'Yes' ? 'yes' : r.req === 'No' ? 'no' : 'opt'}`}>{r.req}</span></td>
                </tr>
            ))}</tbody>
        </table>
    </div>
);

const Flow = ({ steps }) => (
    <div className="hc-flow">
        {steps.map((s, i) => (
            <React.Fragment key={i}>
                <div className="hc-flow__node">{s}</div>
                {i < steps.length - 1 && <FiArrowRight className="hc-flow__arrow" />}
            </React.Fragment>
        ))}
    </div>
);

/* ─── Section Content ─── */
const CONTENT = {
    overview: (
        <div className="hc-article">
            <SectionTitle eyebrow="Getting Started" title="Welcome to Dr. SS Johl Library MS" />
            <p className="hc-lead">This is a complete, web-based Library Management System designed for efficient day-to-day library operations — from adding books to tracking fines automatically.</p>

            <div className="hc-cards-grid">
                {[
                    { icon: <FiBook />, title: 'Book Inventory', desc: 'Manage your entire collection with Accession No, categories, authors and editions.' },
                    { icon: <FiUsers />, title: 'Member System', desc: 'Register students & staff, track their borrow history and manage their status.' },
                    { icon: <FiRefreshCw />, title: 'Circulation', desc: 'Issue books in seconds and process returns with automatic fine calculation.' },
                    { icon: <FiDollarSign />, title: 'Collections', desc: 'View all fine transactions and track pending and collected amounts.' },
                    { icon: <FiBarChart2 />, title: 'Reports', desc: 'Get deep analytics on inventory, member activity and overdue records.' },
                    { icon: <FiCpu />, title: 'Leo AI', desc: 'Ask questions about your library in plain English. Leo queries your live database.' },
                ].map((c, i) => (
                    <div key={i} className="hc-feature-card">
                        <div className="hc-feature-card__icon">{c.icon}</div>
                        <h4>{c.title}</h4>
                        <p>{c.desc}</p>
                    </div>
                ))}
            </div>

            <h2 className="hc-h2">Navigation</h2>
            <p>On <strong>Desktop</strong>: Use the left sidebar to navigate between modules. You can collapse it using the button at the bottom.</p>
            <p style={{marginTop:8}}>On <strong>Mobile</strong>: Tap the blue ☰ button at the top-left to open the navigation drawer. It closes automatically when you select a page.</p>

            <h2 className="hc-h2" style={{marginTop:32}}>User Roles</h2>
            <div className="hc-role-grid">
                <div className="hc-role-card hc-role-card--admin">
                    <h4>Admin</h4>
                    <p>Full system access — can manage users, configure rules, view all reports, and delete data.</p>
                </div>
                <div className="hc-role-card hc-role-card--librarian">
                    <h4>Librarian</h4>
                    <p>Can manage books, members, issue & return books, view reports. Cannot manage staff accounts.</p>
                </div>
            </div>

            <Tip>Use the <strong>Leo AI assistant</strong> (blue button, bottom-right) to instantly query any library data without navigating menus.</Tip>
        </div>
    ),

    dashboard: (
        <div className="hc-article">
            <SectionTitle eyebrow="Dashboard" title="Your Library at a Glance" />
            <p className="hc-lead">The Dashboard is the first page you see after logging in. It shows a real-time snapshot of your library's key performance indicators.</p>

            <h2 className="hc-h2">Stat Cards (Top Row)</h2>
            <div className="hc-info-grid">
                {[
                    { label: 'Total Books', desc: 'Total unique book titles in inventory. Click to jump to Books page.' },
                    { label: 'Total Members', desc: 'All registered members (Active + Inactive). Click to jump to Members.' },
                    { label: 'Active Issues', desc: 'Books currently checked out and not yet returned.' },
                    { label: 'Overdue Books', desc: 'Books whose due date has passed and not returned yet.' },
                ].map((c, i) => (
                    <div key={i} className="hc-info-box">
                        <h4>{c.label}</h4>
                        <p>{c.desc}</p>
                    </div>
                ))}
            </div>

            <h2 className="hc-h2">Recent Transactions</h2>
            <p>The left panel shows your most recent issue and return activity — who borrowed which book and when.</p>

            <h2 className="hc-h2">New Books Added</h2>
            <p>The right panel lists the most recently added books to the collection.</p>

            <h2 className="hc-h2">Quick Stats Panel</h2>
            <p>Shows capacity utilization bars — how many books are issued vs total inventory, and membership distribution.</p>

            <Tip>All stat cards are clickable — they jump directly to the relevant page (Books, Members, or Issue & Return).</Tip>
        </div>
    ),

    books: (
        <div className="hc-article">
            <SectionTitle eyebrow="Books & Inventory" title="Managing Your Book Collection" />
            <p className="hc-lead">The Books module is your central inventory system. Every physical book in your library has a digital record here, identified by a unique Accession Number.</p>

            <h2 className="hc-h2">Viewing Books</h2>
            <p>The main list loads all books in your collection. You can:</p>
            <ul className="hc-ul">
                <li>Search by <strong>Title, Author, or Accession No</strong> using the search box</li>
                <li>Filter by <strong>Category</strong> (e.g., Science, Arts) and <strong>Sub-Category</strong> (e.g., Physics, History)</li>
                <li>Sort any column by clicking the column header</li>
                <li>Click the <FiEye style={{verticalAlign:'middle'}} /> <strong>Eye</strong> icon to see full book details including current issue status</li>
            </ul>

            <h2 className="hc-h2">Adding a New Book</h2>
            <p>Click the <strong>"Add New Book"</strong> button (top-right). Fill in the following fields:</p>
            <FieldTable rows={[
                { field: 'Accession No', desc: 'Unique ID for every physical copy. Must match the physical barcode/label on the book.', req: 'Yes' },
                { field: 'Title', desc: 'Full name of the book', req: 'Yes' },
                { field: 'Author', desc: 'Primary author name', req: 'Yes' },
                { field: 'Category', desc: 'Main subject area (e.g., Computer Science)', req: 'Yes' },
                { field: 'Sub-Category', desc: 'Specific topic (e.g., Python, Database)', req: 'Optional' },
                { field: 'Publisher', desc: 'Publishing house name', req: 'Optional' },
                { field: 'Year', desc: 'Year of publication', req: 'Optional' },
                { field: 'Edition', desc: 'Book edition (e.g., 3rd Edition)', req: 'Optional' },
                { field: 'Classification No', desc: 'Library classification number (Dewey/LC)', req: 'Optional' },
                { field: 'Book Number', desc: 'Internal categorization number', req: 'Optional' },
                { field: 'Pages', desc: 'Number of pages', req: 'Optional' },
                { field: 'Price', desc: 'Purchase price in ₹', req: 'Optional' },
                { field: 'Volume', desc: 'Volume number for multi-volume sets', req: 'Optional' },
                { field: 'Item Type', desc: 'Book / Journal / CD / Other', req: 'Optional' },
                { field: 'Description', desc: 'Summary or abstract of the book', req: 'Optional' },
            ]} />

            <Warn>The <strong>Accession Number</strong> must be unique across all books. You cannot have two books with the same Accession No. This is the primary identifier linked to all issue/return records.</Warn>

            <h2 className="hc-h2">Editing a Book</h2>
            <p>Click the <FiEdit2 style={{verticalAlign:'middle'}} /> <strong>Edit</strong> icon on any row in the book list. All fields can be updated. The Accession No cannot be changed after creation.</p>

            <h2 className="hc-h2">Deleting a Book</h2>
            <p>Click the <FiTrash2 style={{verticalAlign:'middle'}} /> <strong>Delete</strong> icon. Note: If the book is currently issued to a member, it cannot be deleted. Return it first. Historical records (past issues) are preserved even after deletion.</p>

            <h2 className="hc-h2">Book Detail View</h2>
            <p>Click the Eye icon to see a complete panel showing all book metadata. If the book is currently issued, you will see a blue banner showing:</p>
            <ul className="hc-ul">
                <li>Who it's issued to (member name & ID)</li>
                <li>Issue date and Due date</li>
                <li>Accumulated fine (if overdue)</li>
                <li>A "Go to Return" button to quickly process it</li>
            </ul>

            <Tip>Use Category + Sub-Category filters together to instantly narrow down large collections. For example: Category = "Computer Science", Sub-Category = "Python" shows only Python books.</Tip>
        </div>
    ),

    members: (
        <div className="hc-article">
            <SectionTitle eyebrow="Member Management" title="Managing Library Members" />
            <p className="hc-lead">Members are the people who borrow books — students, staff, or faculty. Each member has a unique Member ID and a borrowing history tracked automatically by the system.</p>

            <h2 className="hc-h2">Member Types</h2>
            <div className="hc-role-grid">
                <div className="hc-role-card hc-role-card--admin">
                    <h4>Student</h4>
                    <p>Typically have shorter borrow periods. Standard fine rules apply.</p>
                </div>
                <div className="hc-role-card hc-role-card--librarian">
                    <h4>Staff / Faculty</h4>
                    <p>May have extended borrow periods. Same fine calculation applies.</p>
                </div>
            </div>

            <h2 className="hc-h2">Adding a Member</h2>
            <p>Click <strong>"Add Member"</strong> and fill in:</p>
            <FieldTable rows={[
                { field: 'Name', desc: 'Full name of the member', req: 'Yes' },
                { field: 'Member ID', desc: 'Unique identifier (roll number, employee ID, etc.)', req: 'Yes' },
                { field: 'Member Type', desc: 'Student or Staff', req: 'Yes' },
                { field: 'Phone', desc: 'Contact number', req: 'Optional' },
                { field: 'Email', desc: 'Email address', req: 'Optional' },
                { field: 'Address', desc: 'Physical address', req: 'Optional' },
                { field: 'Status', desc: 'Active or Inactive', req: 'Yes' },
            ]} />

            <h2 className="hc-h2">Member Status</h2>
            <div className="hc-status-row">
                <span className="hc-badge hc-badge--green">Active</span>
                <p>Can borrow books normally via Issue & Return.</p>
            </div>
            <div className="hc-status-row" style={{marginTop:8}}>
                <span className="hc-badge hc-badge--gray">Inactive</span>
                <p>Cannot be selected when issuing a book. Useful for suspended/left members.</p>
            </div>

            <h2 className="hc-h2">Viewing Borrow History</h2>
            <p>Click the Eye icon on any member to open their profile. Switch to the <strong>"History"</strong> tab to see every book they have ever borrowed — including dates, fines, and return status.</p>

            <Warn>You cannot delete a member who has unreturned books. Process the returns first, then proceed with deletion if needed.</Warn>

            <h2 className="hc-h2">Searching Members</h2>
            <p>Use the search bar to find members by Name, Member ID, or Phone number. In the Issue & Return module, only <strong>Active</strong> members appear in search results.</p>
        </div>
    ),

    'issue-return': (
        <div className="hc-article">
            <SectionTitle eyebrow="Issue & Return" title="The Circulation Workflow" />
            <p className="hc-lead">This is the heart of daily library operations. You can issue books to members, process returns, track overdue items, and manage fines — all from one page.</p>

            <h2 className="hc-h2">Issuing a Book</h2>
            <Flow steps={['Find Member', 'Find Book', 'Set Dates', 'Confirm Issue']} />
            <div style={{marginTop:20}}>
                <Step n="1" title="Find Member">
                    Search by Name, Member ID, or Phone. Only <strong>Active</strong> members appear. Click a result to select them.
                </Step>
                <Step n="2" title="Find Book">
                    Use the search bar or filter by Category → Sub-Category to find the book. Only available (not currently issued) books appear. Click a result to select it.
                </Step>
                <Step n="3" title="Set Dates">
                    The <strong>Issue Date</strong> defaults to today. The <strong>Due Date</strong> is automatically calculated based on the Borrow Period setting (configured in Settings → Fine & Rules). You can manually override the due date.
                </Step>
                <Step n="4" title="Confirm Issue">
                    Click the <strong>"Confirm Issue"</strong> button. The system records the transaction and the book status changes to "Issued".
                </Step>
            </div>

            <h2 className="hc-h2">Tabs Overview</h2>
            <FieldTable rows={[
                { field: 'Issue Book', desc: 'The issuing workflow (Steps above)', req: '—' },
                { field: 'Active Issues', desc: 'All books currently checked out and not returned yet', req: '—' },
                { field: 'Returned Items', desc: 'List of all books that have been returned (closed transactions)', req: '—' },
                { field: 'Full History', desc: 'Complete combined log of every issue transaction', req: '—' },
            ]} />

            <h2 className="hc-h2">Processing a Return</h2>
            <p>In the <strong>Active Issues</strong> tab, find the transaction and click the <strong>"Return"</strong> button.</p>
            <p style={{marginTop:8}}>A return dialog opens showing:</p>
            <ul className="hc-ul">
                <li><strong>Calculated Fine</strong> — Auto-computed based on Days Overdue × Daily Fine Rate</li>
                <li><strong>Amount Collected</strong> — How much you actually collected (can be less if waiving fine)</li>
                <li><strong>Remarks</strong> — Note reason for waiving, or general notes</li>
            </ul>
            <p style={{marginTop:8}}>Click <strong>"Finalize Return"</strong> to complete the transaction.</p>

            <h2 className="hc-h2">Fine Calculation Formula</h2>
            <div className="hc-formula">
                <code>Fine = Days Overdue × Fine Per Day</code>
                <span className="hc-formula__cap">Subject to Maximum Fine cap</span>
            </div>
            <p style={{marginTop:12}}>Example: If a book is <strong>5 days overdue</strong>, Fine Per Day = ₹2, Max Fine = ₹100:</p>
            <p><code className="hc-inline-code">Fine = 5 × ₹2 = ₹10</code></p>

            <h2 className="hc-h2">Day Count Colors</h2>
            <div className="hc-status-row"><span className="hc-badge hc-badge--green">+N days</span><p>Book is not yet due — days remaining shown in normal color.</p></div>
            <div className="hc-status-row" style={{marginTop:8}}><span className="hc-badge hc-badge--red">-N days / Overdue</span><p>Book is past due date. Fine is accumulating. Shows in red.</p></div>

            <Warn>If you set Fine Per Day = 0 and Max Fine = 0 in Settings, no fines will be calculated and the system will always show ₹0.</Warn>
            <Tip>You can set the Amount Collected to less than the Calculated Fine if you want to waive part of the fine for a member. Add a Remark explaining the waiver.</Tip>
        </div>
    ),

    collection: (
        <div className="hc-article">
            <SectionTitle eyebrow="Collections" title="Fine & Collection Tracking" />
            <p className="hc-lead">The Collections module gives you a complete view of all fine transactions — both pending and collected. It is the financial audit trail for your library.</p>

            <h2 className="hc-h2">What You'll See</h2>
            <ul className="hc-ul">
                <li><strong>Member Name</strong> — Who the fine is against</li>
                <li><strong>Book Title</strong> — Which book generated the fine</li>
                <li><strong>Fine Amount</strong> — Total calculated fine</li>
                <li><strong>Amount Paid</strong> — What was actually collected</li>
                <li><strong>Balance</strong> — Any remaining unpaid fine</li>
                <li><strong>Collection Date</strong> — When the return was processed</li>
            </ul>

            <h2 className="hc-h2">Filters</h2>
            <p>You can filter by date range and search by member name or accession number to find specific transactions.</p>

            <Tip>Use the Collections report in combination with the Reports module to get period-wise fine summary reports.</Tip>
        </div>
    ),

    reports: (
        <div className="hc-article">
            <SectionTitle eyebrow="Reports" title="Data & Analytics" />
            <p className="hc-lead">The Reports module gives you visual insights and exportable data about your library's performance.</p>

            <h2 className="hc-h2">Available Reports</h2>
            <div className="hc-info-grid">
                {[
                    { label: 'Inventory Summary', desc: 'Total books, breakdown by category, most borrowed titles.' },
                    { label: 'Member Activity', desc: 'Active vs inactive members, top borrowers, member-wise history.' },
                    { label: 'Overdue Report', desc: 'All currently overdue books with member contact information.' },
                    { label: 'Fine Report', desc: 'Total fines generated, collected, and pending for any period.' },
                    { label: 'Circulation Summary', desc: 'Issue and return trends over time.' },
                    { label: 'New Acquisitions', desc: 'Books added in a selected date range.' },
                ].map((r, i) => (
                    <div key={i} className="hc-info-box">
                        <h4>{r.label}</h4>
                        <p>{r.desc}</p>
                    </div>
                ))}
            </div>

            <Tip>Use Leo AI to get instant answers like "Show overdue books" or "Who has the highest fine?" without navigating to Reports.</Tip>
        </div>
    ),

    settings: (
        <div className="hc-article">
            <SectionTitle eyebrow="Settings" title="System Configuration" />
            <p className="hc-lead">Settings is an Admin-only section that controls the global rules governing how fines are calculated and how long members can borrow books.</p>

            <h2 className="hc-h2">Fine & Rules</h2>
            <FieldTable rows={[
                { field: 'Fine Per Day (₹)', desc: 'Amount charged per day after the due date. Set to 0 for no fines.', req: 'Yes' },
                { field: 'Maximum Fine (₹)', desc: 'A cap on how much fine can accumulate. Set to 0 for no cap.', req: 'Yes' },
                { field: 'Default Borrow Period (days)', desc: 'How many days a member can keep a book. Default is 14 days.', req: 'Yes' },
            ]} />

            <Warn>Changes to Fine & Rules take effect on the <strong>next return transaction</strong>. Existing open transactions will use the new rates when they are returned.</Warn>

            <h2 className="hc-h2">Library Identity</h2>
            <p>This section shows the library's name and admin email. These are display-only reference fields currently and do not affect system behavior.</p>

            <Tip>If you do not charge fines, set both "Fine Per Day" and "Maximum Fine" to 0. The system will always calculate ₹0 and the return dialog will still work normally.</Tip>
        </div>
    ),

    users: (
        <div className="hc-article">
            <SectionTitle eyebrow="User Management" title="Managing Staff Accounts" />
            <p className="hc-lead">The Users module is visible and accessible only to <strong>Admins</strong>. It allows you to manage who can log into the Library Management System.</p>

            <Warn>This section controls login access to the system itself — not library members. These are your library staff accounts.</Warn>

            <h2 className="hc-h2">Adding a Staff User</h2>
            <FieldTable rows={[
                { field: 'Username', desc: 'Login username (must be unique)', req: 'Yes' },
                { field: 'Name', desc: 'Full display name of the staff member', req: 'Yes' },
                { field: 'Password', desc: 'Set an initial password. Staff can change it via their Profile.', req: 'Yes' },
                { field: 'Role', desc: 'Admin or Librarian. Controls what they can access.', req: 'Yes' },
            ]} />

            <h2 className="hc-h2">User Roles</h2>
            <p>See the <strong>Roles & Permissions</strong> section for a detailed comparison of what each role can and cannot do.</p>

            <Tip>Create a separate Librarian account for each staff member. This ensures actions in the system (issues, returns) are traceable to individual staff.</Tip>
        </div>
    ),

    leo: (
        <div className="hc-article">
            <SectionTitle eyebrow="Leo AI Assistant" title="Your Library's AI Companion" />
            <p className="hc-lead">Leo is an intelligent, read-only AI assistant embedded in the system. You can ask it questions in plain English and it queries your live database to get answers.</p>

            <div className="hc-leo-intro">
                <div className="hc-leo-avatar"><FiCpu /></div>
                <div>
                    <h3>How to Open Leo</h3>
                    <p>Click the <strong>blue circular button</strong> at the bottom-right corner of your screen. Leo opens as a floating chat window.</p>
                </div>
            </div>

            <h2 className="hc-h2">Leo Controls</h2>
            <FieldTable rows={[
                { field: 'Maximize (⬜)', desc: 'Expands Leo to full-screen mode for better viewing of large tables.', req: '—' },
                { field: 'Refresh (↺)', desc: 'Clears the current chat history and starts a fresh conversation.', req: '—' },
                { field: 'Close (✕)', desc: 'Minimizes Leo back to the floating button.', req: '—' },
            ]} />

            <h2 className="hc-h2">What You Can Ask Leo</h2>
            <div className="hc-chat-examples">
                {[
                    { category: 'Book Inventory', prompts: [
                        '"Show all books by [Author Name]"',
                        '"Is Python Programming available?"',
                        '"How many books do we have in Computer Science?"',
                        '"List books added this month"',
                    ]},
                    { category: 'Members', prompts: [
                        '"Show all active members"',
                        '"Find member with ID 1023"',
                        '"How many students are registered?"',
                        '"List members with pending fines"',
                    ]},
                    { category: 'Issue & Return', prompts: [
                        '"Which books are currently issued?"',
                        '"Show overdue books"',
                        '"What books has [Member Name] borrowed?"',
                        '"How many books are overdue today?"',
                    ]},
                    { category: 'Fines & Finance', prompts: [
                        '"Who has the highest fine?"',
                        '"Show total fines collected this month"',
                        '"List members with unpaid fines"',
                    ]},
                ].map((cat, i) => (
                    <div key={i} className="hc-chat-category">
                        <h4>{cat.category}</h4>
                        <ul className="hc-chat-prompts">
                            {cat.prompts.map((p, j) => <li key={j}><span className="hc-prompt">{p}</span></li>)}
                        </ul>
                    </div>
                ))}
            </div>

            <h2 className="hc-h2">Leo's Limitations</h2>
            <ul className="hc-ul">
                <li>Leo is <strong>read-only</strong> — he cannot add, edit, or delete any data</li>
                <li>Very complex multi-join queries may not always return exact results</li>
                <li>Leo's conversation is remembered within one session only</li>
            </ul>

            <Tip>Leo remembers your conversation context. If you ask about a book and then ask "Is it overdue?", Leo will know which book you mean without you repeating it.</Tip>
        </div>
    ),

    roles: (
        <div className="hc-article">
            <SectionTitle eyebrow="Roles & Permissions" title="Who Can Do What" />
            <p className="hc-lead">The system has two roles: <strong>Admin</strong> and <strong>Librarian</strong>. This matrix shows exactly what each role can and cannot do.</p>

            <div className="hc-matrix">
                <div className="hc-matrix__header">
                    <span>Capability</span>
                    <span>Admin</span>
                    <span>Librarian</span>
                </div>
                {[
                    ['View Dashboard', true, true],
                    ['View & Search Books', true, true],
                    ['Add / Edit Books', true, true],
                    ['Delete Books', true, true],
                    ['View & Search Members', true, true],
                    ['Add / Edit Members', true, true],
                    ['Delete Members', true, true],
                    ['Issue Books', true, true],
                    ['Process Returns', true, true],
                    ['View Collections', true, true],
                    ['View All Reports', true, true],
                    ['Configure Fine Rules (Settings)', true, false],
                    ['Manage Staff Accounts (Users)', true, false],
                    ['Delete Critical System Data', true, false],
                    ['Use Leo AI Assistant', true, true],
                    ['View Help Center', true, true],
                ].map(([cap, admin, lib], i) => (
                    <div key={i} className="hc-matrix__row">
                        <span>{cap}</span>
                        <span>{admin ? <FiCheckCircle className="hc-check" /> : <FiXCircle className="hc-cross" />}</span>
                        <span>{lib ? <FiCheckCircle className="hc-check" /> : <FiXCircle className="hc-cross" />}</span>
                    </div>
                ))}
            </div>
        </div>
    ),

    faq: (
        <div className="hc-article">
            <SectionTitle eyebrow="FAQ" title="Frequently Asked Questions" />

            {[
                {
                    q: 'Can I issue multiple books to the same member?',
                    a: 'Yes. As long as the member is Active and the book is available, you can issue multiple books one at a time.',
                },
                {
                    q: 'What happens if I set Fine Per Day to 0?',
                    a: 'No fines will be calculated. When processing a return, the fine will always show ₹0. Set both Fine Per Day and Max Fine to 0 if you do not charge late fees.',
                },
                {
                    q: 'Can I change the Accession Number of a book?',
                    a: 'No. The Accession Number is the permanent primary ID and cannot be changed after creation. It is linked to all issue & return history records.',
                },
                {
                    q: 'What does "Days Left" show as red in Active Issues?',
                    a: 'Red means the book is overdue — the due date has already passed. A fine may be accumulating if your fine rules are configured.',
                },
                {
                    q: 'How do I find a book that is currently issued?',
                    a: 'Open the book from the Books list and click the Eye icon. If issued, a blue banner shows the member name, issue date, due date, and current fine.',
                },
                {
                    q: 'Can a Librarian create other staff accounts?',
                    a: 'No. Only Admins can access the User Management section and create or modify staff login accounts.',
                },
                {
                    q: 'Why does Leo not show the mobile keyboard header?',
                    a: 'This is a browser behavior on mobile — when the keyboard opens, the viewport shrinks. The Leo chat window is designed to adapt. The blue header remains pinned to the top of the visible screen.',
                },
                {
                    q: 'How does Leo remember context?',
                    a: 'Leo saves your conversation in your browser\'s local storage. The conversation persists across page navigations. Use the Refresh (↺) button in Leo\'s header to start a new conversation.',
                },
                {
                    q: 'Can I partially waive a fine?',
                    a: 'Yes! In the Return dialog, the "Amount Collected" field is editable. Set it to less than the "Calculated Fine" to collect a partial payment. Add a note in Remarks explaining the waiver.',
                },
                {
                    q: 'What is the difference between Member Management and User Management?',
                    a: 'Members are library patrons (students, staff) who borrow books. Users are system login accounts for library staff (Admins, Librarians) who operate this software.',
                },
            ].map((item, i) => (
                <div key={i} className="hc-faq-item">
                    <h4 className="hc-faq-q"><FiChevronRight className="hc-faq-icon" />{item.q}</h4>
                    <p className="hc-faq-a">{item.a}</p>
                </div>
            ))}
        </div>
    ),
};


/* ─── Main Component ─── */
const HelpCenter = () => {
    const [activeSection, setActiveSection] = useState('overview');
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    const handleNav = (id) => {
        setActiveSection(id);
        setMobileNavOpen(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const currentSection = SECTIONS.find(s => s.id === activeSection);

    return (
        <div className="hc-root">
            {/* Mobile Overlay */}
            {mobileNavOpen && (
                <div className="hc-overlay" onClick={() => setMobileNavOpen(false)} />
            )}

            {/* Mobile Top Bar */}
            <div className="hc-mobile-topbar">
                <button className="hc-mobile-menu-btn" onClick={() => setMobileNavOpen(true)}>
                    <FiMenu />
                </button>
                <span className="hc-mobile-title">{currentSection?.label || 'Help Center'}</span>
            </div>

            <div className="hc-layout">
                {/* Sidebar */}
                <aside className={`hc-sidebar ${mobileNavOpen ? 'hc-sidebar--open' : ''}`}>
                    <div className="hc-sidebar__header">
                        <div className="hc-sidebar__logo">
                            <FiBook />
                        </div>
                        <div>
                            <h2 className="hc-sidebar__title">Help Center</h2>
                            <p className="hc-sidebar__sub">Dr. SS Johl Library</p>
                        </div>
                        <button className="hc-sidebar__close" onClick={() => setMobileNavOpen(false)}>
                            <FiX />
                        </button>
                    </div>

                    <nav className="hc-sidebar__nav">
                        {SECTIONS.map(s => (
                            <button
                                key={s.id}
                                className={`hc-nav-item ${activeSection === s.id ? 'hc-nav-item--active' : ''}`}
                                onClick={() => handleNav(s.id)}
                            >
                                <span className="hc-nav-item__icon">{s.icon}</span>
                                <span className="hc-nav-item__label">{s.label}</span>
                                {s.badge && (
                                    <span className={`hc-nav-item__badge hc-nav-item__badge--${s.badge === 'Admin' ? 'admin' : s.badge === 'AI' ? 'ai' : s.badge === 'Core' ? 'core' : 'default'}`}>
                                        {s.badge}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>

                    <div className="hc-sidebar__footer">
                        <p>v2.0 · © 2026 WhizInfo</p>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="hc-main">
                    <div className="hc-main__inner">
                        {CONTENT[activeSection] || (
                            <div className="hc-article">
                                <p>Select a section from the left menu.</p>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default HelpCenter;
